/**
 * Scene Editor — General Settings.
 * Read-only section hierarchy + inspector for gameConfig.options.
 * Same paths as classic General Settings.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import _ from "lodash";
import { TextField } from "@material-ui/core";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import InnerComponentIcon from "../../../images/gameConfig/Inner-component-icon.svg";
import {
  BAR_BG,
  BooleanConfigField,
  FieldRow,
  NumberConfigField,
  PANEL_BG,
  SECTION_DIVIDER,
  SectionHeader,
} from "./sceneEditorInspector";
import { SceneEditorDefaultVersion, SceneEditorResetActionRow } from "./sceneEditorVersionMeta";
import { isOptionRelocatedFromGeneral } from "../../modules/sceneEditorRelocatedOptions";
import { rewriteComponentPathForLocalizationOverride } from "../../modules/localizationOverrides";
import { sceneEditorWriteNumberPath } from "../../modules/sceneEditorGestureWrite";

const SPLIT_STORAGE_KEY = "sceneEditorGeneralSettingsSplit";
const INPUT_STYLE = { color: "#fff", fontSize: 12 };

const TREE_SX = {
  overflowX: "hidden",
  flexGrow: 1,
  "& .MuiTreeItem-content": {
    padding: "0 4px",
    borderRadius: 0,
    minHeight: "22px",
    gap: 0,
  },
  "& .MuiTreeItem-content:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.055)",
  },
  "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused": {
    backgroundColor: "rgba(144, 202, 249, 0.20)",
  },
  "& .MuiTreeItem-content.Mui-selected:hover": {
    backgroundColor: "rgba(144, 202, 249, 0.26)",
  },
  "& .MuiTreeItem-content.Mui-focused": {
    backgroundColor: "transparent",
  },
  "& .MuiTreeItem-iconContainer": {
    width: 0,
    margin: 0,
    display: "none",
  },
  "& .MuiTreeItem-label": {
    paddingLeft: 0,
  },
};

function SectionLabel({ caption }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", minHeight: "22px", flex: 1, minWidth: 0 }}
    >
      <img
        src={InnerComponentIcon}
        alt=""
        style={{ width: 13, height: 13, marginRight: 6, flexShrink: 0, opacity: 0.85 }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "13px",
          fontWeight: 600,
          color: "#e0e0e0",
        }}
      >
        {caption}
      </span>
    </div>
  );
}

function hasOption(version, key) {
  return _.has(version, `gameConfig.options.${key}`);
}

function optionValue(version, key, fallback) {
  return _.get(version, `gameConfig.options.${key}`, fallback);
}

function clamp(n, min, max) {
  let v = Number(n);
  if (!Number.isFinite(v)) v = min;
  if (Number.isFinite(min)) v = Math.max(min, v);
  if (Number.isFinite(max)) v = Math.min(max, v);
  return v;
}

function hasLocalizationLink(version) {
  return _.has(version, "gameConfig.options.localizationLink");
}

function localizationLinkExists(version, path) {
  const value = _.get(version, `gameConfig.options.localizationLink.${path}`);
  return value != null && value !== "";
}

function androidLinkPath(version, language) {
  if (!hasLocalizationLink(version)) return "gameConfig.options.link.android";
  return `gameConfig.options.localizationLink.${language}.android`;
}

function iosLinkPath(version, language) {
  if (!hasLocalizationLink(version)) return "gameConfig.options.link.ios";
  return `gameConfig.options.localizationLink.${language}.ios`;
}

function androidLinkValue(version, language) {
  if (!hasLocalizationLink(version)) {
    return _.get(version, "gameConfig.options.link.android", "");
  }
  if (!localizationLinkExists(version, `${language}.android`)) {
    return _.get(version, "gameConfig.options.localizationLink.en.android", "");
  }
  return _.get(version, `gameConfig.options.localizationLink.${language}.android`, "");
}

function iosLinkValue(version, language) {
  if (!hasLocalizationLink(version)) {
    return _.get(version, "gameConfig.options.link.ios", "");
  }
  if (!localizationLinkExists(version, `${language}.ios`)) {
    return _.get(version, "gameConfig.options.localizationLink.en.ios", "");
  }
  return _.get(version, `gameConfig.options.localizationLink.${language}.ios`, "");
}

function listGeneralSections(version) {
  const sections = [{ id: "links", caption: "Forward Links" }];
  if (hasOption(version, "isEndCardEnabled")) {
    sections.push({ id: "endCard", caption: "End Card" });
  }
  if (hasOption(version, "isGoToMarketEnabled")) {
    sections.push({ id: "goToMarket", caption: "Go To Market" });
  }
  if (
    (hasOption(version, "isInGameCTAEnabled") &&
      !isOptionRelocatedFromGeneral("isInGameCTAEnabled")) ||
    (hasOption(version, "isEntireScreenCTAEnabled") &&
      !isOptionRelocatedFromGeneral("isEntireScreenCTAEnabled"))
  ) {
    sections.push({ id: "cta", caption: "Call To Action" });
  }
  if (
    (hasOption(version, "isSoundEnabled") && !isOptionRelocatedFromGeneral("isSoundEnabled")) ||
    hasOption(version, "isTutorialEnabled") ||
    hasOption(version, "isAutoplayEnabled") ||
    (hasOption(version, "isBannerEnabled") && !isOptionRelocatedFromGeneral("isBannerEnabled")) ||
    hasOption(version, "shouldForcePortraitMode")
  ) {
    sections.push({ id: "game", caption: "Game" });
  }
  sections.push({ id: "versionSettings", caption: "Defaults & Resets" });
  return sections;
}

function SwitchField({ label, value, disabled, onChange, readOnly }) {
  return (
    <FieldRow label={label} readOnly={readOnly}>
      <BooleanConfigField
        value={value}
        disabled={readOnly || disabled}
        onChange={onChange}
      />
    </FieldRow>
  );
}

function NumberField({ label, value, min, max, step, onChange, readOnly }) {
  return (
    <FieldRow label={label} readOnly={readOnly}>
      <NumberConfigField
        value={value}
        disabled={readOnly}
        step={step || 1}
        min={min}
        max={max}
        onChange={(n, opts) => onChange(clamp(n, min, max), opts)}
      />
    </FieldRow>
  );
}

function TextValueField({ label, value, onChange, readOnly }) {
  return (
    <FieldRow label={label} readOnly={readOnly}>
      <TextField
        size="small"
        fullWidth
        disabled={readOnly}
        value={value ?? ""}
        InputProps={{ style: INPUT_STYLE }}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldRow>
  );
}

export default function containerSceneEditorGeneralSettings(props) {
  const containerRef = useRef(null);
  const hierarchyPaneRef = useRef(null);
  const inspectorPaneRef = useRef(null);
  const [availHeight, setAvailHeight] = useState(0);
  const [treeRatio, setTreeRatio] = useState(() => {
    const raw = window.localStorage.getItem(SPLIT_STORAGE_KEY);
    const n = raw != null ? Number(raw) : 0.38;
    return Number.isFinite(n) ? Math.max(0.2, Math.min(0.8, n)) : 0.38;
  });
  const version = props.selectedVersion;
  const language = _.get(version, "gameConfig.options.language", "en");
  const readOnly = props.userPermissions?.update === false || props.isVersionLocked === true;
  const sections = listGeneralSections(version);
  const [selectedSectionId, setSelectedSectionId] = useState(() => sections[0]?.id || "links");

  useEffect(() => {
    setSelectedSectionId(listGeneralSections(props.selectedVersion)[0]?.id || "links");
  }, [_.get(props, "selectedBase._id")]);

  useEffect(() => {
    if (!sections.some((s) => s.id === selectedSectionId) && sections[0]) {
      setSelectedSectionId(sections[0].id);
    }
  }, [sections, selectedSectionId]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    function findScrollParent(node) {
      let p = node.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if (oy === "auto" || oy === "scroll") return p;
        p = p.parentElement;
      }
      return null;
    }
    const sp = findScrollParent(el);
    function measure() {
      const rect = el.getBoundingClientRect();
      if (rect.top === 0 && rect.height === 0) return;
      const bottom = sp
        ? sp.getBoundingClientRect().top + sp.clientHeight
        : window.innerHeight - 8;
      const next = Math.max(240, Math.floor(bottom - rect.top));
      setAvailHeight((prev) => (prev === next ? prev : next));
    }
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      if (sp) ro.observe(sp);
      if (el.parentElement) ro.observe(el.parentElement);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  function write(path, value, options) {
    if (readOnly) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    if (options && (options.gesture || options.gestureEnd)) {
      sceneEditorWriteNumberPath({
        path,
        value,
        options,
        setValue: (p) => props.setSelectedVersionPathValue(p),
        selectedVersion: props.selectedVersion,
        selectedBase: props.selectedBase,
        selectedLanguage: _.get(props.selectedVersion, "gameConfig.options.language", "en"),
        isLocalization: false,
        rewritePayload: rewriteComponentPathForLocalizationOverride,
      });
      return;
    }
    props.setSelectedVersionPathValue({ path, value });
  }

  function writeOption(key, value, options) {
    write(`gameConfig.options.${key}`, value, options);
  }

  function startDividerDrag(e) {
    e.preventDefault();
    const hierarchyPane = hierarchyPaneRef.current;
    const inspectorPane = inspectorPaneRef.current;
    if (!hierarchyPane || !inspectorPane) return;
    const startY = e.clientY;
    const startRatio = treeRatio;
    const splitHeight =
      hierarchyPane.getBoundingClientRect().height +
      inspectorPane.getBoundingClientRect().height;
    if (splitHeight <= 0) return;
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "row-resize";
    hierarchyPane.style.pointerEvents = "none";
    inspectorPane.style.pointerEvents = "none";
    function onMove(ev) {
      const next = Math.max(0.2, Math.min(0.8, startRatio + (ev.clientY - startY) / splitHeight));
      setTreeRatio(next);
    }
    function onUp() {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      hierarchyPane.style.pointerEvents = "";
      inspectorPane.style.pointerEvents = "";
      setTreeRatio((r) => {
        window.localStorage.setItem(SPLIT_STORAGE_KEY, String(r));
        return r;
      });
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  const selectedSection = sections.find((s) => s.id === selectedSectionId) || sections[0];

  return (
    <div
      ref={containerRef}
      className="scene-editor-props-panel proto-props-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        height: availHeight ? `${availHeight}px` : "80vh",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: PANEL_BG,
      }}
    >
      <style>{`
        .scene-editor-props-panel .MuiInputBase-root {
          background-color: #212226;
          border: 1px solid #3a3a3f;
          border-radius: 4px;
          min-height: 24px;
        }
        .scene-editor-props-panel .MuiInputBase-root.Mui-focused {
          border-color: #64b5f6;
        }
        .scene-editor-props-panel .MuiInputBase-input {
          padding: 3px 6px;
          font-size: 12px;
        }
        .scene-editor-props-panel .MuiInput-underline:before,
        .scene-editor-props-panel .MuiInput-underline:after {
          display: none;
        }
        .scene-editor-props-panel .MuiSwitch-root {
          margin: 0;
        }
      `}</style>

      <div
        ref={hierarchyPaneRef}
        style={{
          flexGrow: treeRatio,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: PANEL_BG,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            backgroundColor: BAR_BG,
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>Hierarchy</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 8px" }}>
          <SimpleTreeView
            aria-label="general-settings-hierarchy"
            selectedItems={selectedSectionId ? [selectedSectionId] : []}
            onSelectedItemsChange={(event, itemIds) => {
              const raw = itemIds == null ? [] : Array.isArray(itemIds) ? itemIds : [itemIds];
              if (raw[0]) setSelectedSectionId(raw[0]);
            }}
            sx={TREE_SX}
          >
            {sections.map((section) => (
              <TreeItem
                key={section.id}
                itemId={section.id}
                label={<SectionLabel caption={section.caption} />}
              />
            ))}
          </SimpleTreeView>
        </div>
      </div>

      <div
        onPointerDown={startDividerDrag}
        style={{
          flex: "0 0 auto",
          height: 6,
          cursor: "row-resize",
          backgroundColor: "#2b2b2b",
          borderTop: "1px solid #1f1f1f",
          borderBottom: "1px solid #1f1f1f",
        }}
      />

      <div
        ref={inspectorPaneRef}
        style={{
          flexGrow: 1 - treeRatio,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: PANEL_BG,
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            padding: "10px 12px",
            backgroundColor: BAR_BG,
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>
            {selectedSection?.caption || "General Settings"}
          </span>
          <span style={{ fontSize: 11, color: "#8a8d93" }}>options</span>
        </div>
        <SectionHeader title="Properties" />
        <div style={{ padding: "2px 4px 16px" }}>
          {selectedSectionId === "links" ? (
            <>
              <TextValueField
                label="Android Link"
                readOnly={readOnly}
                value={androidLinkValue(version, language)}
                onChange={(value) => write(androidLinkPath(version, language), value)}
              />
              <TextValueField
                label="iOS Link"
                readOnly={readOnly}
                value={iosLinkValue(version, language)}
                onChange={(value) => write(iosLinkPath(version, language), value)}
              />

            </>
          ) : null}

          {selectedSectionId === "endCard" ? (
            <>
              <SwitchField
                label="Enable End Card"
                readOnly={readOnly}
                disabled={optionValue(version, "isGoToMarketEnabled", false)}
                value={optionValue(version, "isEndCardEnabled", true)}
                onChange={(value, opts) => writeOption("isEndCardEnabled", value, opts)}
              />
              {hasOption(version, "goToEndCardLevel") ? (
                <NumberField
                  label="Go To End Card Level"
                  readOnly={readOnly}
                  value={optionValue(version, "goToEndCardLevel", 0)}
                  min={0}
                  max={2}
                  onChange={(value, opts) => writeOption("goToEndCardLevel", value, opts)}
                />
              ) : null}
              {hasOption(version, "endCardDelay") ? (
                <NumberField
                  label="End Card Delay"
                  readOnly={readOnly}
                  value={optionValue(version, "endCardDelay", 1)}
                  min={0}
                  max={10}
                  onChange={(value, opts) => writeOption("endCardDelay", value, opts)}
                />
              ) : null}
              {hasOption(version, "endCardSeconds") ? (
                <NumberField
                  label="Show End Card After X Seconds"
                  readOnly={readOnly}
                  value={optionValue(version, "endCardSeconds", 60)}
                  min={0}
                  max={60}
                  onChange={(value, opts) => writeOption("endCardSeconds", value, opts)}
                />
              ) : null}
              {hasOption(version, "endCardTaps") ? (
                <NumberField
                  label="Show End Card After X Taps"
                  readOnly={readOnly}
                  value={optionValue(version, "endCardTaps", 60)}
                  min={0}
                  max={200}
                  onChange={(value, opts) => writeOption("endCardTaps", value, opts)}
                />
              ) : null}
              {hasOption(version, "endCardMoves") ? (
                <NumberField
                  label="Show End Card After X Moves"
                  readOnly={readOnly}
                  value={optionValue(version, "endCardMoves", 30)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("endCardMoves", value, opts)}
                />
              ) : null}
              {hasOption(version, "endCardMovesPlus") ? (
                <NumberField
                  label="Show End Card After X Moves Plus"
                  readOnly={readOnly}
                  value={optionValue(version, "endCardMovesPlus", 30)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("endCardMovesPlus", value, opts)}
                />
              ) : null}
              <NumberField
                label="Auto Forward to Store After X Seconds"
                readOnly={readOnly}
                value={optionValue(version, "endCardAutoForwardToStoreSeconds", 30)}
                min={0}
                max={30}
                onChange={(value, opts) => writeOption("endCardAutoForwardToStoreSeconds", value, opts)}
              />
              {hasOption(version, "isEntireScreenCTAEnabled") ? (
                <SwitchField
                  label="Entire Screen CTA"
                  readOnly={readOnly}
                  value={optionValue(version, "isEntireScreenCTAEnabled", false)}
                  onChange={(value, opts) => writeOption("isEntireScreenCTAEnabled", value, opts)}
                />
              ) : null}
            </>
          ) : null}

          {selectedSectionId === "goToMarket" ? (
            <>
              <SwitchField
                label="Enable Market"
                readOnly={readOnly}
                disabled={optionValue(version, "isEndCardEnabled", true)}
                value={optionValue(version, "isGoToMarketEnabled", false)}
                onChange={(value, opts) => writeOption("isGoToMarketEnabled", value, opts)}
              />
              {hasOption(version, "showEndCardAfterMarket") ? (
                <SwitchField
                  label="Show End Card After Market"
                  readOnly={readOnly}
                  value={optionValue(version, "showEndCardAfterMarket", false)}
                  onChange={(value, opts) => writeOption("showEndCardAfterMarket", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreForEveryMove") ? (
                <SwitchField
                  label="Open Store For Every Move"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreForEveryMove", false)}
                  onChange={(value, opts) => writeOption("openStoreForEveryMove", value, opts)}
                />
              ) : null}
              {hasOption(version, "showEndCardAfterGameplayForMarket") ? (
                <SwitchField
                  label="Show End Card After Gameplay For Market"
                  readOnly={readOnly}
                  value={optionValue(version, "showEndCardAfterGameplayForMarket", false)}
                  onChange={(value, opts) => writeOption("showEndCardAfterGameplayForMarket", value, opts)}
                />
              ) : null}
              {hasOption(version, "goToMarketLevel") ? (
                <NumberField
                  label="Go To Market Level"
                  readOnly={readOnly}
                  value={optionValue(version, "goToMarketLevel", 0)}
                  min={0}
                  max={2}
                  onChange={(value, opts) => writeOption("goToMarketLevel", value, opts)}
                />
              ) : null}
              {hasOption(version, "goToMarketTaps") ? (
                <NumberField
                  label="Go To Market After X Taps"
                  readOnly={readOnly}
                  value={optionValue(version, "goToMarketTaps", 60)}
                  min={0}
                  max={120}
                  onChange={(value, opts) => writeOption("goToMarketTaps", value, opts)}
                />
              ) : null}
              {hasOption(version, "goToMarketSeconds") ? (
                <NumberField
                  label="Go To Market After X Seconds"
                  readOnly={readOnly}
                  value={optionValue(version, "goToMarketSeconds", 60)}
                  min={0}
                  max={60}
                  onChange={(value, opts) => writeOption("goToMarketSeconds", value, opts)}
                />
              ) : null}
              {hasOption(version, "goToMarketMoves") ? (
                <NumberField
                  label="Go To Market After X Moves"
                  readOnly={readOnly}
                  value={optionValue(version, "goToMarketMoves", 30)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("goToMarketMoves", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreForEveryMoveAfterXTaps") ? (
                <NumberField
                  label="Go To Market For Every Move After X Taps"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreForEveryMoveAfterXTaps", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("openStoreForEveryMoveAfterXTaps", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreForEveryMoveAfterXMoves") ? (
                <NumberField
                  label="Go To Market After X Moves"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreForEveryMoveAfterXMoves", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("openStoreForEveryMoveAfterXMoves", value, opts)}
                />
              ) : null}
              {hasOption(version, "goToMarketMovesPlus") ? (
                <NumberField
                  label="Go To Market After X Moves Plus"
                  readOnly={readOnly}
                  value={optionValue(version, "goToMarketMovesPlus", 30)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("goToMarketMovesPlus", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreForEveryXMove") ? (
                <SwitchField
                  label="Open Store For Every X Move/Tap"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreForEveryXMove", false)}
                  onChange={(value, opts) => writeOption("openStoreForEveryXMove", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreEveryXMoveCount") ? (
                <NumberField
                  label="Open Store Every X Move Count"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreEveryXMoveCount", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("openStoreEveryXMoveCount", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreForEveryXMoveStartMoveCount") ? (
                <NumberField
                  label="Open Store Every X Move Start Move Count"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreForEveryXMoveStartMoveCount", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("openStoreForEveryXMoveStartMoveCount", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreEveryXTapCount") ? (
                <NumberField
                  label="Open Store Every X Tap Count"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreEveryXTapCount", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("openStoreEveryXTapCount", value, opts)}
                />
              ) : null}
              {hasOption(version, "openStoreForEveryXTapStartTapCount") ? (
                <NumberField
                  label="Open Store Every X Tap Start Tap Count"
                  readOnly={readOnly}
                  value={optionValue(version, "openStoreForEveryXTapStartTapCount", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("openStoreForEveryXTapStartTapCount", value, opts)}
                />
              ) : null}
              {hasOption(version, "startNextLevelAfterMarket") ? (
                <SwitchField
                  label="Start Next Level After Market"
                  readOnly={readOnly}
                  value={optionValue(version, "startNextLevelAfterMarket", false)}
                  onChange={(value, opts) => writeOption("startNextLevelAfterMarket", value, opts)}
                />
              ) : null}
              {hasOption(version, "startNextLevelAfterMarketMoveCount") ? (
                <NumberField
                  label="Start Next Level After Market Move Count"
                  readOnly={readOnly}
                  value={optionValue(version, "startNextLevelAfterMarketMoveCount", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("startNextLevelAfterMarketMoveCount", value, opts)}
                />
              ) : null}
              {hasOption(version, "startNextLevelAfterMarketTapCount") ? (
                <NumberField
                  label="Start Next Level After Market Tap Count"
                  readOnly={readOnly}
                  value={optionValue(version, "startNextLevelAfterMarketTapCount", 0)}
                  min={0}
                  max={100}
                  onChange={(value, opts) => writeOption("startNextLevelAfterMarketTapCount", value, opts)}
                />
              ) : null}
            </>
          ) : null}

          {selectedSectionId === "cta" ? (
            <>
              {hasOption(version, "isEntireScreenCTAEnabled") &&
                !isOptionRelocatedFromGeneral("isEntireScreenCTAEnabled") ? (
                <SwitchField
                  label="Entire Screen CTA"
                  readOnly={readOnly}
                  value={optionValue(version, "isEntireScreenCTAEnabled", false)}
                  onChange={(value, opts) => writeOption("isEntireScreenCTAEnabled", value, opts)}
                />
              ) : null}
              {hasOption(version, "isInGameCTAEnabled") &&
                !isOptionRelocatedFromGeneral("isInGameCTAEnabled") ? (
                <SwitchField
                  label="In Game CTA"
                  readOnly={readOnly}
                  value={optionValue(version, "isInGameCTAEnabled", false)}
                  onChange={(value, opts) => writeOption("isInGameCTAEnabled", value, opts)}
                />
              ) : null}
            </>
          ) : null}

          {selectedSectionId === "game" ? (
            <>
              {hasOption(version, "isSoundEnabled") &&
                !isOptionRelocatedFromGeneral("isSoundEnabled") ? (
                <SwitchField
                  label="Sound"
                  readOnly={readOnly}
                  value={optionValue(version, "isSoundEnabled", false)}
                  onChange={(value) => {
                    writeOption("isSoundEnabled", value);
                    try {
                      document.getElementById("gamePreviewIframe").focus();
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                />
              ) : null}
              {hasOption(version, "isTutorialEnabled") ? (
                <SwitchField
                  label="Tutorial"
                  readOnly={readOnly}
                  value={optionValue(version, "isTutorialEnabled", false)}
                  onChange={(value, opts) => writeOption("isTutorialEnabled", value, opts)}
                />
              ) : null}
              {hasOption(version, "isAutoplayEnabled") ? (
                <SwitchField
                  label="Autoplay"
                  readOnly={readOnly}
                  value={optionValue(version, "isAutoplayEnabled", false)}
                  onChange={(value, opts) => writeOption("isAutoplayEnabled", value, opts)}
                />
              ) : null}
              {hasOption(version, "isBannerEnabled") &&
                !isOptionRelocatedFromGeneral("isBannerEnabled") ? (
                <SwitchField
                  label="Banner"
                  readOnly={readOnly}
                  value={optionValue(version, "isBannerEnabled", false)}
                  onChange={(value, opts) => writeOption("isBannerEnabled", value, opts)}
                />
              ) : null}
              {hasOption(version, "shouldForcePortraitMode") ? (
                <SwitchField
                  label="Force Portrait Mode"
                  readOnly={readOnly}
                  value={optionValue(version, "shouldForcePortraitMode", false)}
                  onChange={(value, opts) => writeOption("shouldForcePortraitMode", value, opts)}
                />
              ) : null}
            </>
          ) : null}

          {selectedSectionId === "versionSettings" ? (
            <>
              {_.get(props, "userCompany.isPermittedSetDefault") === true ? (
                <SceneEditorDefaultVersion
                  selectedVersion={props.selectedVersion}
                  getDefaultVersion={props.getDefaultVersion}
                  setDefaultVersion={props.setDefaultVersion}
                  setSearchBarValue={props.setSearchBarValue}
                  defaultVersion={props.defaultVersion}
                  defaultVersionUpdated={props.defaultVersionUpdated}
                  setDefaultVersionUpdated={props.setDefaultVersionUpdated}
                  versionRename={props.versionRename}
                />
              ) : null}
              <SceneEditorResetActionRow
                buttonId="resetVersionSettings"
                disabled={props.isVersionLocked}
                label="Version Settings"
                hint="Restore version defaults"
                buttonLabel="Reset"
                onReset={props.onResetVersionSettings}
              />
              <SceneEditorResetActionRow
                buttonId="resetCategorization"
                disabled={props.isVersionLocked}
                label="Categorization"
                hint="Restore categorization layout defaults"
                buttonLabel="Reset"
                onReset={props.onResetCategorization}
              />
              <SceneEditorResetActionRow
                buttonId="resetLocalizationSettings"
                disabled={props.isVersionLocked}
                label="Localization Settings"
                hint="Restore localization defaults"
                buttonLabel="Reset"
                onReset={props.onResetLocalizationSettings}
              />
              <SceneEditorResetActionRow
                buttonId="resetGeneralSettings"
                disabled={props.isVersionLocked}
                label="General Settings"
                hint="Restore general defaults"
                buttonLabel="Reset"
                onReset={props.onResetGeneralSettings}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
